// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chat, ChevronDown } from '@carbon/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { usePersona } from '../../hooks/usePersona';
import { useShellStore, type Persona } from '../../stores/shellStore';
import * as authService from '../../services/authService';
import { useActiveSite } from '../../hooks/useActiveSite';

const HELIOS_MARK = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--helios)"
    strokeWidth="1.6"
  >
    <circle cx="12" cy="12" r="4.2" fill="var(--helios)" stroke="none" />
    <path
      d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9L17 7M7 17l-2.1 2.1"
      strokeLinecap="round"
    />
  </svg>
);

const PERSONA_OPTIONS: { id: Persona; label: string; sub: string }[] = [
  { id: 'operator', label: 'Operator view', sub: 'Single site · work alarms' },
  { id: 'manager', label: 'Ops Manager view', sub: 'Fleet · escalate & oversee' },
  { id: 'executive', label: 'Executive view', sub: 'Portfolio · posture only' },
];

export default function HeliosHeader() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { persona } = usePersona();
  const {
    siteId,
    siteLabel: activeSiteLabel,
    options,
    canSwitch,
    isFleet,
    setSite,
    setFleet,
  } = useActiveSite();
  const setPersonaOverride = useShellStore(s => s.setPersonaOverride);
  const openAskHelios = useShellStore(s => s.openAskHelios);
  const [menuOpen, setMenuOpen] = useState(false);
  const [siteMenuOpen, setSiteMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const siteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
      if (siteRef.current && !siteRef.current.contains(e.target as Node)) setSiteMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const name = user ? `${user.first_name} ${user.last_name}`.trim() : 'Operator';
  const initials = (user?.first_name?.[0] ?? 'O') + (user?.last_name?.[0] ?? '');

  // Operators are locked to one site; managers/admins pick from a dropdown.
  const siteLabel = persona === 'executive' ? 'Portfolio posture' : activeSiteLabel;
  const siteSub =
    persona === 'operator'
      ? 'Single site · day shift'
      : persona === 'executive'
        ? 'All regions · read-only'
        : isFleet
          ? 'All sites · region'
          : 'Switch site';

  const handleLogout = () => {
    setMenuOpen(false);
    authService.logout();
  };

  const handleSelectSite = (id: string) => {
    setSite(id);
    setSiteMenuOpen(false);
    navigate(`/site/${id}/kg`);
  };

  const handleSelectFleet = () => {
    setFleet();
    setSiteMenuOpen(false);
    navigate('/fleet');
  };

  return (
    <header className="helios-header">
      <div className="helios-header-left">
        <div className="helios-brand">
          {HELIOS_MARK}
          <span className="helios-brand-name">Helios</span>
        </div>
        <div className="helios-header-divider" />
        {canSwitch ? (
          <div ref={siteRef} style={{ position: 'relative' }}>
            <button
              type="button"
              className="helios-header-btn"
              onClick={() => setSiteMenuOpen(o => !o)}
              aria-haspopup="listbox"
              aria-expanded={siteMenuOpen}
              title="Switch site"
            >
              <div style={{ textAlign: 'left', lineHeight: 1.15 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{siteLabel}</div>
                <div className="site-sub">{siteSub}</div>
              </div>
              <ChevronDown size={16} />
            </button>
            {siteMenuOpen && (
              <div className="helios-popover" role="listbox">
                <button
                  type="button"
                  role="option"
                  aria-selected={isFleet}
                  className={`helios-popover-item${isFleet ? ' active' : ''}`}
                  onClick={handleSelectFleet}
                >
                  Whole fleet
                  <span className="sub">All sites</span>
                </button>
                <div className="helios-popover-divider" />
                {options.map(opt => {
                  const selected = !isFleet && opt.siteId === siteId;
                  return (
                    <button
                      key={opt.siteId}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`helios-popover-item${selected ? ' active' : ''}`}
                      onClick={() => handleSelectSite(opt.siteId)}
                    >
                      {opt.label}
                      <span className="sub">{opt.siteId}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="helios-site-indicator">
            <span className="site-name">{siteLabel}</span>
            <span className="site-sub">{siteSub}</span>
          </div>
        )}
      </div>

      <div className="helios-header-right">
        <button
          type="button"
          className="helios-header-btn"
          onClick={() => openAskHelios(null)}
          title="Ask Helios"
        >
          <Chat size={16} />
          <span>Ask Helios</span>
        </button>

        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            type="button"
            className="helios-icon-btn"
            onClick={() => setMenuOpen(o => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title={name}
          >
            <span className="helios-avatar">{initials.toUpperCase()}</span>
          </button>

          {menuOpen && (
            <div className="helios-popover" style={{ right: 0 }} role="menu">
              <div style={{ padding: '10px 12px' }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-helper)' }}>
                  {persona === 'operator'
                    ? 'Site Operator'
                    : persona === 'manager'
                      ? 'Ops Manager'
                      : 'Executive'}
                </div>
              </div>
              <div className="helios-popover-divider" />
              {PERSONA_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  className={`helios-popover-item${persona === opt.id ? ' active' : ''}`}
                  onClick={() => {
                    setPersonaOverride(opt.id);
                    setMenuOpen(false);
                    // Route to "/" and let PersonaHome pick the new role's home.
                    navigate('/');
                  }}
                >
                  {opt.label}
                  <span className="sub">{opt.sub}</span>
                </button>
              ))}
              <div className="helios-popover-divider" />
              <button
                type="button"
                className="helios-popover-item"
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/prefs');
                }}
              >
                Preferences
              </button>
              <button type="button" className="helios-popover-item" onClick={handleLogout}>
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
